import { BarChart3, Eye, MousePointerClick, Package } from 'lucide-react';
import { useState } from 'react';

import {
  useMetricasParceirosControllerDeProdutos,
  useMetricasParceirosControllerDoParceiro,
  useMetricasParceirosControllerRanking,
} from '@/api/generated/admin-métricas/admin-métricas';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { Card, CardContent } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const JANELAS = [7, 30, 90] as const;
type Janela = (typeof JANELAS)[number];

/**
 * Métricas de parceiro (B8-10).
 *
 * Substitui as 7 RPCs do Postgres que a V1 chamava direto do browser
 * (`useParceiroMetricas.ts`) por três rotas com permissão.
 *
 * ⚠️ **Sparkline em CSS, e o repo AGORA tem biblioteca de gráfico.** A frase que
 * estava aqui — "o frontend novo ainda não tem nenhuma dependência de chart" —
 * deixou de valer com a Análise Comparativa, que instalou o `recharts`. O
 * precedente que se decidiu não abrir, porém, era o de carregar ~90 KB no
 * bundle principal: lá a rota é `React.lazy`, e esta não é. Uma barra por dia
 * em CSS continua sendo o custo certo para esta tela.
 */
export function MetricasParceirosPage() {
  const [dias, setDias] = useState<Janela>(30);
  const [parceiroId, setParceiroId] = useState<string | null>(null);

  const { data, isPending, isError, refetch } = useMetricasParceirosControllerRanking({ dias });
  const { data: produtos } = useMetricasParceirosControllerDeProdutos({ dias });

  if (isError) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-brand" />
            Métricas de parceiros
          </h2>
          <p className="text-muted-foreground mt-1">
            Impressões, cliques e importações para receituário.
          </p>
        </div>

        <div className="flex gap-1 p-1 bg-brand/10 rounded-lg">
          {JANELAS.map((opcao) => (
            <button
              key={opcao}
              type="button"
              onClick={() => setDias(opcao)}
              aria-pressed={dias === opcao}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                dias === opcao
                  ? 'bg-brand text-brand-foreground shadow-sm'
                  : 'text-brand hover:bg-brand/10',
              )}
            >
              {opcao} dias
            </button>
          ))}
        </div>
      </div>

      {isPending ? (
        <Skeleton className="h-96 w-full rounded-xl" />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Total icone={Eye} rotulo="Impressões" valor={data.totais.impressoes} />
            <Total icone={MousePointerClick} rotulo="Cliques" valor={data.totais.cliques} />
            <Total icone={Package} rotulo="Importações" valor={data.totais.importacoes} />
          </div>

          {data.itens.length === 0 ? (
            <EstadoVazio
              icone={BarChart3}
              titulo="Sem parceiros"
              descricao="Cadastre parceiros para acompanhar o desempenho deles."
            />
          ) : (
            <div className="rounded-xl border border-border overflow-hidden bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Parceiro</TableHead>
                    <TableHead className="text-right">Impressões</TableHead>
                    <TableHead className="text-right">Cliques</TableHead>
                    <TableHead className="text-right">Taxa</TableHead>
                    <TableHead className="text-right">Importações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((linha) => (
                    <TableRow
                      key={linha.parceiroId}
                      onClick={() =>
                        setParceiroId(parceiroId === linha.parceiroId ? null : linha.parceiroId)
                      }
                      className="cursor-pointer"
                    >
                      <TableCell className="font-medium">{linha.parceiroNome}</TableCell>
                      <TableCell className="text-right">{linha.impressoes}</TableCell>
                      <TableCell className="text-right">{linha.cliques}</TableCell>
                      <TableCell className="text-right">
                        {/* "—" e não "0%": sem impressão não há base para calcular,
                            e um zero afirmaria que ninguém clicou. */}
                        {linha.taxaDeClique === null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          `${linha.taxaDeClique.toFixed(1)}%`
                        )}
                      </TableCell>
                      <TableCell className="text-right">{linha.importacoes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {parceiroId && <SerieDoParceiro parceiroId={parceiroId} dias={dias} />}

          {produtos && produtos.itens.length > 0 && (
            <section>
              <h3 className="text-lg font-semibold text-foreground mb-3">Produtos mais importados</h3>
              <div className="rounded-xl border border-border overflow-hidden bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produto</TableHead>
                      <TableHead>Parceiro</TableHead>
                      <TableHead className="text-right">Importações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {produtos.itens.slice(0, 10).map((linha) => (
                      <TableRow key={linha.produtoId}>
                        <TableCell className="font-medium">{linha.produtoNome}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {linha.parceiroNome}
                        </TableCell>
                        <TableCell className="text-right">{linha.importacoes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                A importação é contada quando o veterinário traz o produto para um receituário.
                Fica em zero enquanto o módulo de receituários não existir.
              </p>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Total({
  icone: Icone,
  rotulo,
  valor,
}: {
  icone: typeof Eye;
  rotulo: string;
  valor: number;
}) {
  return (
    <Card className="border-border">
      <CardContent className="p-5 flex items-center gap-4">
        <div className="p-2.5 rounded-lg bg-gradient-nohra">
          <Icone className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{rotulo}</p>
          <p className="text-2xl font-bold text-foreground">{valor.toLocaleString('pt-BR')}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/** Sparkline em CSS: uma barra por dia, altura relativa ao pico da janela. */
function SerieDoParceiro({ parceiroId, dias }: { parceiroId: string; dias: Janela }) {
  const { data, isPending } = useMetricasParceirosControllerDoParceiro(parceiroId, { dias });

  if (isPending) return <Skeleton className="h-40 w-full rounded-xl" />;
  if (!data) return null;

  const pico = Math.max(1, ...data.serie.map((p) => p.impressoes + p.cliques));

  return (
    <Card className="border-border">
      <CardContent className="p-5">
        <h3 className="font-semibold text-foreground">{data.parceiroNome}</h3>
        <p className="text-xs text-muted-foreground">
          {data.totais.impressoes} impressões · {data.totais.cliques} cliques nos últimos{' '}
          {data.dias} dias
        </p>

        <div className="mt-4 flex items-end gap-0.5 h-24">
          {data.serie.map((ponto) => {
            const total = ponto.impressoes + ponto.cliques;
            return (
              <div
                key={ponto.dia}
                title={`${new Date(`${ponto.dia}T00:00:00Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}: ${ponto.impressoes} impressões, ${ponto.cliques} cliques`}
                className="flex-1 bg-brand/20 rounded-t relative min-h-[2px]"
                style={{ height: `${(total / pico) * 100}%` }}
              >
                {ponto.cliques > 0 && (
                  <div
                    className="absolute bottom-0 inset-x-0 bg-brand rounded-t"
                    style={{ height: `${(ponto.cliques / Math.max(1, total)) * 100}%` }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
          <span>{data.serie[0]?.dia}</span>
          <span>{data.serie.at(-1)?.dia}</span>
        </div>
      </CardContent>
    </Card>
  );
}
